import { fade, hex, lighten, type Color } from "../../common/color.js";
import { insets, type Rect } from "../../common/geometry.js";
import type { RenderContext } from "../render/RenderContext.js";
import { BaseTheme, derivePalette, makeMetrics } from "./BaseTheme.js";
import type { CaptionButton, VisualState, WindowVisualState } from "./Theme.js";

/**
 * "Imperial" — the far-future military console: hard square corners, gunmetal
 * grays, thin cold borders and a single hazard-red accent. Every panel gets a
 * greeble strip, the little rectangular detail that sells the look.
 */

const RED = hex("#d33a3f");
const STEEL = hex("#8b95a3");
const IMPERIAL_FONT = "'Bahnschrift', 'Segoe UI', system-ui, sans-serif";

export class ImperialTheme extends BaseTheme {
  constructor() {
    super({
      name: "imperial",
      isDark: true,
      metrics: makeMetrics({
        titleBarHeight: 30,
        windowBorder: 1,
        windowCornerRadius: 0,
        resizeGripSize: 5,
        captionButtonWidth: 42,
        controlCornerRadius: 0,
        controlHeight: 28,
        buttonPadding: insets(16, 5),
        buttonMinWidth: 92,
        focusRingWidth: 1,
        focusRingInset: -3,
        textBoxPadding: insets(9, 5),
        checkBoxSize: 16,
        radioSize: 16,
        scrollBarSize: 13,
        scrollBarMinThumb: 30,
        listItemHeight: 24,
        headerHeight: 27,
        font: { family: IMPERIAL_FONT, size: 13 },
        captionFont: { family: IMPERIAL_FONT, size: 13, weight: 600 },
        monoFont: { family: "Consolas, monospace", size: 12.5 },
      }),
      palette: derivePalette({
        desktop: hex("#0a0b0d"),
        surface: hex("#17191d"),
        raised: hex("#22262c"),
        field: hex("#0e1013"),
        ink: hex("#dfe3e8"),
        accent: RED,
        accentText: hex("#ffffff"),
        border: hex("#3a4049"),
        titleBar: hex("#1e2126"),
        titleText: hex("#eef1f4"),
        isDark: true,
        overrides: {
          windowBorderActive: hex("#565e6a"),
          windowBorderInactive: hex("#2b3037"),
          textSubtle: STEEL,
          scrollTrack: hex("#0e1013"),
          scrollThumb: hex("#3d434c"),
          scrollThumbHover: hex("#4e5661"),
          scrollThumbPressed: RED,
          headerBg: hex("#1c1f24"),
          headerText: STEEL,
          gridLine: hex("#2a2f36"),
          selectionBg: RED,
          selectionText: hex("#ffffff"),
          selectionInactiveBg: hex("#3a2226"),
          textSelectionBg: fade(RED, 0.4),
        },
      }),
    });
  }

  /** The greeble strip occupies the first ~28px of the caption. */
  protected override get titleTextInset(): number {
    return 36;
  }

  /** A red rule under the title bar, plus a greeble strip on the left. */
  protected override decorateWindow(rc: RenderContext, bounds: Rect, s: WindowVisualState): void {
    const b = this.metrics.windowBorder;
    const h = this.captionHeight(s);
    const accent = s.active ? RED : hex("#4a2a2c");
    rc.fillRect({ x: b, y: b + h - 2, width: bounds.width - b * 2, height: 2 }, accent);

    // Three stacked bars: the panel-line detail that reads as machinery.
    const y = b + 8;
    for (let i = 0; i < 3; i++) {
      rc.fillRect(
        { x: b + 10 + i * 6, y, width: 3, height: h - 18 },
        s.active ? STEEL : hex("#4a5058"),
      );
    }
  }

  protected override decorateButton(rc: RenderContext, r: Rect, s: VisualState): void {
    if (s.disabled) return;
    // A notch out of the top-left corner, the way panelled hardware is chamfered.
    const ink = s.isDefault === true ? hex("#ffffff") : s.hovered ? RED : STEEL;
    rc.fillRect({ x: r.x, y: r.y, width: 6, height: 2 }, ink);
    rc.fillRect({ x: r.x, y: r.y, width: 2, height: 6 }, ink);
    rc.fillRect({ x: r.x + r.width - 6, y: r.y + r.height - 2, width: 6, height: 2 }, ink);
    rc.fillRect({ x: r.x + r.width - 2, y: r.y + r.height - 6, width: 2, height: 6 }, ink);
  }

  protected override captionButtonHighlight(button: CaptionButton, pressed: boolean): Color {
    if (button === "close") return pressed ? hex("#96262a") : RED;
    return pressed ? hex("#31363d") : hex("#2a2e35");
  }

  override drawColumnHeader(rc: RenderContext, r: Rect, s: VisualState): void {
    const p = this.palette;
    rc.fillRect(r, s.pressed ? p.controlBgPressed : s.hovered ? p.headerBgHover : p.headerBg);
    rc.hLine(r.x, r.y + r.height - 1, r.width, s.hovered ? RED : p.gridLine);
    rc.vLine(r.x + r.width - 1, r.y + 4, r.height - 8, p.gridLine);
  }

  override drawFocusRing(rc: RenderContext, r: Rect): void {
    const i = this.metrics.focusRingInset;
    rc.strokeRect(
      { x: r.x + i, y: r.y + i, width: r.width - i * 2, height: r.height - i * 2 },
      lighten(RED, 0.1),
      1,
      0,
    );
  }
}
